/**
 * PaymentFields — Dynamic payment reference fields based on payment method.
 *
 * Shows contextual fields:
 * - ESPECES: nothing extra
 * - CHEQUE: N° chèque (required) + Banque (required)
 * - VIREMENT: Réf. virement (required) + Banque (required)
 * - MOBILE_MONEY_*: N° téléphone (required) + Transaction ID (required)
 */
import React from 'react';
import { usePaymentMethods } from '../src/hooks/usePaymentMethods';

export interface PaymentDetails {
  mode_paiement: string;
  reference: string;
  banque: string;
  numero_telephone: string;
  transaction_id: string;
}

export const EMPTY_PAYMENT_DETAILS: PaymentDetails = {
  mode_paiement: 'ESPECES',
  reference: '',
  banque: '',
  numero_telephone: '',
  transaction_id: '',
};

interface PaymentFieldsProps {
  value: PaymentDetails;
  onChange: (value: PaymentDetails) => void;
}

export const PaymentMethodSelect = ({ value, onChange }: PaymentFieldsProps) => {
  const methods = usePaymentMethods();
  const mode = value.mode_paiement;
  const isMobile = mode.startsWith('MOBILE_MONEY');
  const isCheque = mode === 'CHEQUE';
  const isVirement = mode === 'VIREMENT';
  const needsBank = isCheque || isVirement;

  const update = (field: keyof PaymentDetails, val: string) => {
    onChange({ ...value, [field]: val });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Mode de paiement <span className="text-red-500">*</span>
        </label>
        <select
          className="w-full border-slate-300 rounded-md text-sm py-2"
          value={mode}
          onChange={e => onChange({ ...EMPTY_PAYMENT_DETAILS, mode_paiement: e.target.value })}
        >
          {methods.map(m => (
            <option key={m.code} value={m.code}>{m.label}</option>
          ))}
        </select>
      </div>

      {isCheque && (
        <>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              N° de chèque <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-slate-300 rounded-md text-sm p-2"
              value={value.reference}
              onChange={e => update('reference', e.target.value)}
              placeholder="Ex: 0012345678"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Banque <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-slate-300 rounded-md text-sm p-2"
              value={value.banque}
              onChange={e => update('banque', e.target.value)}
              placeholder="Ex: BOA, Ecobank, SGBBE..."
            />
          </div>
        </>
      )}

      {isVirement && (
        <>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Référence virement <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-slate-300 rounded-md text-sm p-2"
              value={value.reference}
              onChange={e => update('reference', e.target.value)}
              placeholder="Réf. ou n° de transfert"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Banque émettrice <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-slate-300 rounded-md text-sm p-2"
              value={value.banque}
              onChange={e => update('banque', e.target.value)}
              placeholder="Ex: BOA, Ecobank, SGBBE..."
            />
          </div>
        </>
      )}

      {isMobile && (
        <>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              N° de téléphone <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-slate-300 rounded-md text-sm p-2"
              value={value.numero_telephone}
              onChange={e => update('numero_telephone', e.target.value)}
              placeholder="Ex: +229 97 00 00 00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              ID Transaction <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full border border-slate-300 rounded-md text-sm p-2"
              value={value.transaction_id}
              onChange={e => update('transaction_id', e.target.value)}
              placeholder="Ex: TXN-2026032600123"
            />
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Validate that required fields are filled based on payment method.
 */
export function isPaymentValid(details: PaymentDetails): boolean {
  const mode = details.mode_paiement;
  if (mode === 'ESPECES') return true;
  if (mode === 'CHEQUE') return !!(details.reference && details.banque);
  if (mode === 'VIREMENT') return !!(details.reference && details.banque);
  if (mode.startsWith('MOBILE_MONEY')) return !!(details.numero_telephone && details.transaction_id);
  return true; // Unknown mode — don't block
}
